
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import SEOMetadata from "@/components/SEOMetadata";
import HeroSection from "@/components/HeroSection";
import ContactForm from "@/components/contact/ContactForm";
import ContactInfo from "@/components/contact/ContactInfo";

import { Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import TrustElements from "@/components/contact/TrustElements";
import ContactIntro from "@/components/contact/ContactIntro";
import BookingSection from "@/components/contact/BookingSection";
import { useLocation } from "react-router-dom";
import { webPageStructuredData } from "@/data/structuredData";

const Kontakt: React.FC = () => {
  const location = useLocation();

  // Generate canonical URL without query parameters
  const canonicalUrl = `https://joerg-hoermann.de/kontakt`;

  return (
    <main>
      <SEOMetadata
        metaTitle="Erstgespräch Coaching & Consulting | Jörg Hörmann"
        metaDescription="Kostenloses Erstgespräch für Executive Coaching, Finanzcoaching oder Consulting. 30 Min. Kennenlernen – unverbindlich. ✆ 09171-9892118"
        pageName="Kontakt"
        primaryLink="/kontakt"
        canonicalUrl={canonicalUrl}
        imageUrl="/lovable-uploads/21de3789-ed78-4fd4-9bae-abb205041b23.png"
        title="Executive Coaching & Finanzcoaching Erstgespräch"
        description="Kostenloses Erstgespräch vereinbaren"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Kontakt & Erstgespräch – Jörg Hörmann",
            description: "Kostenloses Erstgespräch für Executive Coaching, Finanzcoaching oder Consulting. 30 Minuten zum Kennenlernen.",
            url: "https://joerg-hoermann.de/kontakt"
          }))}
        </script>
      </Helmet>

      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Vereinbaren Sie ein kostenloses 30-minütiges Erstgespräch mit Jörg Hörmann für Executive Coaching, Finanzcoaching oder strategische Begleitung. Unverbindlich und auf Augenhöhe – in der Metropolregion Nürnberg oder remote.</p>
      </section>

      <HeroSection
        title="Executive Coaching & Finanzcoaching"
        subtitle="Kostenloses Erstgespräch vereinbaren" 
        description="30 Minuten, um uns kennenzulernen und herauszufinden, ob wir zusammenpassen. Ganz unverbindlich und auf Augenhöhe."
        backgroundClass="bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-100/80"
        hideHeroLogo={true}
        portraitMode={false}
      />

      {/* Direkter CTA nach Hero */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-slate-100/80 to-white">
        <div className="container px-4 mx-auto">
          <div className="max-w-xl mx-auto text-center">
            <div className="bg-gradient-to-br from-white to-slate-50/40 border border-slate-200/60 rounded-xl p-8 shadow-sm">
              <div className="flex items-center justify-center mb-4">
                <Clock className="h-5 w-5 text-joerg-600 mr-2" />
                <span className="text-slate-700 font-medium">30 Minuten Kennenlernen</span>
              </div>
              
              <div className="space-y-2 mb-6 text-left text-sm">
                <p className="text-slate-700 flex items-start">
                  <span className="text-primary mr-3 mt-1">•</span>
                  Führungskräfteentwicklung & Executive Consulting
                </p>
                <p className="text-slate-700 flex items-start">
                  <span className="text-primary mr-3 mt-1">•</span>
                  Finanzcoaching für Privatpersonen
                </p>
              </div>
              
              <Button 
                onClick={() => {
                  window.open('https://zeeg.me/joerghoermann', '_blank', 'noopener,noreferrer');
                }}
                size="lg" 
                className="w-full bg-cta hover:bg-cta-hover active:bg-cta-active text-white rounded-lg py-4 text-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-cta/25 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <Calendar className="mr-2 h-5 w-5 flex-shrink-0" />
                <span className="sm:hidden">Termin vereinbaren</span>
                <span className="hidden sm:inline">Gesprächstermin vereinbaren</span>
              </Button>
              
              <p className="text-sm text-slate-500 mt-4">
                Wählen Sie Ihren Wunschtermin direkt im Kalender
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-gradient-to-b from-slate-50/50 to-white border-t border-slate-200/60">
        <div className="container px-4 mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">Alternativ per Nachricht</h2>
                <p className="text-slate-600 mb-6">
                  Für erste Informationen oder spezifische Fragen.
                </p>
                <ContactForm />
              </div>
              <div className="lg:col-span-1">
                <ContactInfo />
                <TrustElements />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Kontakt;
